import { ChallengeService, PartnerService, TaskService, UserService } from '@duck/api-definitions'
import axios, { type AxiosRequestConfig } from 'axios'

// Configure axios defaults to send credentials with all requests 🏴‍☠️⭐
axios.defaults.withCredentials = true

// For now, just use a simple baseOptions without Configuration wrapper
// The API client should accept baseOptions directly in constructor
const baseOptions = {
  withCredentials: true, // Essential for sending HTTP-only cookies! 🏴‍☠️⭐
  isJsonMime: () => false
}

export const apis = {
  userService: new UserService.DefaultApi(baseOptions, `/api/users`),
  taskService: new TaskService.DefaultApi(baseOptions, `/api/tasks`),
  challengeService: new ChallengeService.DefaultApi(baseOptions, `/api/challenges`),
  partnerService: new PartnerService.DefaultApi(baseOptions, `/api/partners`)
}

// Function to set up axios interceptors - to be called from React component with proper token access 🏴‍☠️⭐
export const setupAxiosInterceptor = (
  getAccessToken: () => string | null,
  setAccessToken: (token: string | null) => void,
  setUser: (user: UserService.User) => void,
  logout: () => void,
  clearAuthError?: () => void
) => {
  type RetriableAxiosRequestConfig = AxiosRequestConfig & { _isRetry?: boolean }
  let refreshPromise: Promise<{ user: UserService.User; accessToken: string }> | null = null

  const getOrCreateRefreshPromise = () => {
    if (!refreshPromise) {
      refreshPromise = apis.userService
        .authRefreshPost()
        .then(({ data }) => {
          const { user, accessToken } = data
          setAccessToken(accessToken)
          setUser(user)
          clearAuthError?.()
          return { user, accessToken }
        })
        .catch((refreshError) => {
          logout()
          throw refreshError
        })
        .finally(() => {
          refreshPromise = null
        })
    }
    return refreshPromise
  }

  // Add request interceptor with proper token access ⭐🏴‍☠️
  const requestInterceptorId = axios.interceptors.request.use((config) => {
    const accessToken = getAccessToken()
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  })

  // Add response interceptor for automatic session refresh 🏴‍☠️⭐
  const responseInterceptorId = axios.interceptors.response.use(
    // Success responses pass through unchanged
    (response) => response,
    // Handle error responses
    async (error) => {
      const originalRequest = error.config as RetriableAxiosRequestConfig
      
      // Check if this is a 401 error with "Session validation failed" message
      if (
        error.response?.status === 401 && 
        error.response?.data?.error === "Session validation failed" &&
        !originalRequest._isRetry // Prevent infinite retry loops
      ) {
        try {
          // Mark this request as a retry attempt
          originalRequest._isRetry = true

          const { accessToken } = await getOrCreateRefreshPromise()
          
          // Update the authorization header for the original request
          if (originalRequest.headers) {
            if ('set' in originalRequest.headers && typeof originalRequest.headers.set === 'function') {
              originalRequest.headers.set('Authorization', `Bearer ${accessToken}`)
            } else {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`
            }
          } else {
            originalRequest.headers = { Authorization: `Bearer ${accessToken}` }
          }

          // Retry the original request with the new token
          return axios(originalRequest)
          
        } catch {
          // Return the original error so components can handle the failed auth state
          return Promise.reject(error)
        }
      }
      
      // For all other errors, return as-is
      return Promise.reject(error)
    }
  )

  return () => {
    axios.interceptors.request.eject(requestInterceptorId)
    axios.interceptors.response.eject(responseInterceptorId)
  }
}
